import { EvaluationFormType } from 'domains/signupDirect/schemas/evaluationSchema';
import { FC, useCallback } from 'react';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { useFieldErrors } from 'utils/forms/fieldErrors';
import ManualPCPEntry from './ManualPCPEntry';
import { NpiPhysician } from '../NPI/NPILookup';
import { AutoPCPFormType, PCPInputMode } from '../pcpSchemas';
import { SearchPCPForm } from './SearchNPI';
import { DIRECT_SIGNUP_MIXPANEL_EVENTS } from 'domains/signupDirect/misc/mixpanelTracking';
import { useTrack } from 'utils/hooks/useMixPanel';
import { useSignupDirectCtx } from 'domains/signupDirect/context';
import { JotformRedirectReason } from 'domains/signupDirect/misc/jotform/urls';
import { useDialog } from '@expressable/syntax-ds';
import { DirectSignupNoPCPInfoRequiredDialog } from 'components/Dialog/DirectSignupNoPCPInfoDialog';

/** note(juan) Top level component for PCP information input for signup ONLY. Handles 3 variants and paths:
 * - Preloaded PCP: When PCP data is already provided by the eligibility check
 * - Search: User can search for PCP in the NPI database
 * - Manual: User can enter PCP details manually
 *
 *
 * This component is NOT self contained and not ready to be reused outside of this very specific signup context. Will require
 * a refactor if we want to use it elsewhere.
 */

export const PCPInformationInput: FC = () => {
  const { isResumedLead, onContinueToJotformRegistrationForm } = useSignupDirectCtx();
  const { dialogStateProps, onOpen, onClose: onCloseDialog } = useDialog();
  const { setValue } = useFormContext<DeepPartial<EvaluationFormType>>();
  const pcpData = useWatch<DeepPartial<EvaluationFormType>, 'primaryPhysician'>({
    name: 'primaryPhysician',
  });

  const { track } = useTrack();
  useTrack(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.EVAL.PCP.PCP_REQUIRED);

  const { errors: formErrors } = useFormState();

  const onBailoutNoPPIDialog = useCallback(() => {
    track(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.EVAL.FORM.NO_PCP_BAILOUT, { isResumedLead });
    onContinueToJotformRegistrationForm(JotformRedirectReason.NO_PCP_BAILOUT);

    onCloseDialog();
  }, []);

  const isError = useFieldErrors('primaryPhysician.npiNumber', formErrors) !== null;

  const onChangeNPIPCP = (npiPhysician?: NpiPhysician) => {
    if (npiPhysician) {
      track(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.EVAL.PCP.PCP_FOUND);
      setValue('primaryPhysician.npiNumber', npiPhysician.npiNumber);
      setValue('primaryPhysician.fullName', npiPhysician.fullName);
      setValue('primaryPhysician.phone', npiPhysician.phone);
      setValue('primaryPhysician.office', npiPhysician.office);
    } else {
      setValue('primaryPhysician.npiNumber', undefined);
    }
  };

  const onSwitchToSearchMode = useCallback(() => {
    setValue('primaryPhysician.mode', PCPInputMode.Auto);
  }, [setValue]);

  const onSwitchToManualMode = useCallback(() => {
    track(DIRECT_SIGNUP_MIXPANEL_EVENTS.STEPS.EVAL.PCP.PCP_MANUAL);
    setValue('primaryPhysician.mode', PCPInputMode.Manual);
  }, [setValue]);

  const hasPickedPCP = pcpData?.npiNumber !== undefined;

  return (
    <>
      <DirectSignupNoPCPInfoRequiredDialog {...dialogStateProps} onBailout={onBailoutNoPPIDialog} />

      {pcpData?.mode === PCPInputMode.Manual && (
        <ManualPCPEntry onBailoutNoPCP={onOpen} onBackToSearch={onSwitchToSearchMode} />
      )}
      {pcpData?.mode === PCPInputMode.Auto && (
        <SearchPCPForm
          onTriggerManualMode={onSwitchToManualMode}
          onBailoutNoPCP={onOpen}
          onChangeNPIPCP={onChangeNPIPCP}
          currentPCPData={hasPickedPCP ? (pcpData as AutoPCPFormType) : undefined}
          isError={isError}
        />
      )}
    </>
  );
};
